import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import {
  ChevronDown,
  ChevronRight,
  Filter as FilterIcon,
  GripVertical,
  Plus,
  Search,
} from 'lucide-react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { MemberAvatar } from '@/components/MemberAvatar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { formatDue } from '@/lib/dates';
import { formatRelativeTime } from '@/lib/relative-time';
import { cn } from '@/lib/utils';

/* ─────────────────────────────────────────────────────────────────────────
 * Glass redesign — Phase 4 List view.
 *
 * Direct port of `app/fluxter/project/list.jsx` + `list.css`. DOM
 * structure, class semantics, and exact CSS values mirror the prototype.
 *
 * Composition (top → bottom):
 *   .v1-list            — root flex column
 *   .v1-list-subbar     — group-by pill · search · count
 *   .v1-list-colhead    — sticky column header row (sortable cells)
 *   group sections      — collapsible, tinted by category
 *     .v1-list-row × N  — two-line rows
 *
 * Generic over the data adapter the page provides — see `TaskRow`
 * for the full row shape.
 * ───────────────────────────────────────────────────────────────────── */

export type GroupByKey = 'status' | 'assignee' | 'priority' | 'due' | 'project' | 'none';

export type Priority = 'urgent' | 'high' | 'medium' | 'low' | 'none';

export type StatusCategory = 'todo' | 'in_progress' | 'complete';

export interface TaskRow {
  id: string;
  code: string;
  title: string;
  statusId: string;
  statusName: string;
  statusColor: string;
  statusCategory: StatusCategory;
  priority: Priority;
  estimateMinutes: number | null;
  loggedMinutes: number;
  assigneeId: string | null;
  assigneeName: string | null;
  assigneeEmail: string | null;
  dueAt: string | null;
  tagIds: string[];
  parentTaskId: string | null;
  position: number;
  commentCount: number;
  pullRequestCount: number;
  subtaskCount: number;
  updatedAt: string;
  /** Workspace mode only (cross-project listing) */
  projectId?: string | null;
  projectName?: string | null;
  projectColor?: string | null;
  projectCode?: string | null;
}

export interface StatusOption {
  id: string;
  name: string;
  color: string;
  category: StatusCategory;
  /** Used by the inline composer to pick the right status when the
   *  user adds a task to a group keyed by status category. */
  isDefault: boolean;
}

/**
 * Field overrides handed to `onCreateTask`. Each group-by mode maps
 * its group key to a different subset:
 *   status   → status_id (resolved from the lane's default)
 *   assignee → assignee_id (set when the group is a real member;
 *              omitted entirely for "__unassigned" — the column is
 *              already nullable + defaults null on insert)
 *   priority → priority
 *   due/project → no override; plain todo task
 */
export interface CreateTaskOverrides {
  title: string;
  status_id?: string;
  assignee_id?: string;
  priority?: Priority;
}

export interface MemberOption {
  id: string;
  name: string | null;
  email: string | null;
}

export interface TagOption {
  id: string;
  name: string;
  color: string;
}

export interface TaskListProps {
  tasks: TaskRow[];
  workspaceSlug: string;
  statuses: StatusOption[];
  members: MemberOption[];
  tags: TagOption[];
  onOpenTask: (taskUlid: string) => void;
  /** Patch a single field on a task (status / assignee). */
  onUpdateTask: (taskUlid: string, patch: { status_id?: string; assignee_id?: string | null }) => void;
  /** Reorder a task within its sibling group. */
  onReorderTask: (taskUlid: string, newPosition: number) => void;
  /**
   * Optional create-task handler triggered by a group header's "+ Add"
   * affordance. The TaskList resolves the active group → field overrides
   * (status/assignee/priority) and hands the merged shape to the page's
   * `mutateAsync`. Returns a promise so the inline composer can await
   * and re-focus for back-to-back additions.
   */
  onCreateTask?: (overrides: CreateTaskOverrides) => Promise<void>;
  /** Optional: enable workspace-mode columns (project chip). */
  isWorkspace?: boolean;
  /** Default group-by; user can change at runtime. */
  initialGroupBy?: GroupByKey;
  /** Density tweak — passed through to the .is-dense style hooks. */
  density?: 'comfortable' | 'dense';
}

/* ── Group definitions — mirror the prototype's GROUP_BY object ──────── */

interface GroupSpec {
  key: string;
  label: string;
  /** Hex color or CSS var for the group dot. */
  color: string;
  /** Used by `.v1-list-group-{classKey}` for swimlane tints. */
  classKey?: StatusCategory;
}

const SWIMLANE_ORDER: Array<{ key: StatusCategory; label: string; color: string }> = [
  { key: 'todo',        label: 'To do',       color: '#94a3b8' },
  { key: 'in_progress', label: 'In progress', color: '#3b82f6' },
  { key: 'complete',    label: 'Complete',    color: '#10b981' },
];

const PRIORITY_ORDER: Priority[] = ['urgent', 'high', 'medium', 'low', 'none'];

const PRIORITY_INFO: Record<Priority, { name: string; color: string; glyph: string }> = {
  none:   { name: 'No priority', color: '#94a3b8', glyph: '—' },
  low:    { name: 'Low',         color: '#64748b', glyph: '·' },
  medium: { name: 'Medium',      color: '#3b82f6', glyph: '◔' },
  high:   { name: 'High',        color: '#f59e0b', glyph: '◑' },
  urgent: { name: 'Urgent',      color: '#ef4444', glyph: '◉' },
};

interface GroupByDef {
  label: string;
  groups: (ctx: GroupCtx) => GroupSpec[];
  ofTask: (t: TaskRow) => string;
}

interface GroupCtx {
  members: MemberOption[];
  isWorkspace: boolean;
  tasks: TaskRow[];
}

const groupByDefs: Record<Exclude<GroupByKey, 'none'>, GroupByDef> = {
  status: {
    label: 'Status',
    groups: () =>
      SWIMLANE_ORDER.map((l) => ({ key: l.key, label: l.label, color: l.color, classKey: l.key })),
    ofTask: (t) => t.statusCategory,
  },
  assignee: {
    label: 'Assignee',
    groups: ({ members }) => [
      ...members.map((m) => ({ key: m.id, label: m.name ?? m.email ?? 'Unknown', color: '#94a3b8' })),
      { key: '__unassigned', label: 'Unassigned', color: '#94a3b8' },
    ],
    ofTask: (t) => t.assigneeId ?? '__unassigned',
  },
  priority: {
    label: 'Priority',
    groups: () =>
      PRIORITY_ORDER.map((k) => ({ key: k, label: PRIORITY_INFO[k].name, color: PRIORITY_INFO[k].color })),
    ofTask: (t) => t.priority,
  },
  due: {
    label: 'Due date',
    groups: () => [
      { key: 'overdue', label: 'Overdue',     color: '#ef4444' },
      { key: 'today',   label: 'Today',       color: '#f59e0b' },
      { key: 'week',    label: 'This week',   color: '#3b82f6' },
      { key: 'later',   label: 'Later',       color: '#94a3b8' },
      { key: 'nodue',   label: 'No due date', color: '#94a3b8' },
    ],
    ofTask: (t) => {
      if (!t.dueAt) return 'nodue';
      const due = new Date(t.dueAt);
      const days = Math.round((due.getTime() - new Date().setHours(0, 0, 0, 0)) / 86_400_000);
      if (days < 0) return 'overdue';
      if (days === 0) return 'today';
      if (days <= 7) return 'week';
      return 'later';
    },
  },
  project: {
    label: 'Project',
    groups: ({ tasks }) => {
      const seen = new Map<string, GroupSpec>();
      for (const t of tasks) {
        const id = t.projectId ?? '__no_project__';
        if (!seen.has(id)) {
          seen.set(id, {
            key: id,
            label: t.projectName ?? 'No project',
            color: t.projectColor ?? '#94a3b8',
          });
        }
      }
      return [...seen.values()];
    },
    ofTask: (t) => t.projectId ?? '__no_project__',
  },
};

/* ── Sortable columns ────────────────────────────────────────────────── */

type SortKey = 'code' | 'title' | 'priority' | 'due';

const SORTABLE: Record<SortKey, (a: TaskRow, b: TaskRow) => number> = {
  code: (a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }),
  title: (a, b) => a.title.localeCompare(b.title),
  priority: (a, b) =>
    PRIORITY_ORDER.indexOf(a.priority) - PRIORITY_ORDER.indexOf(b.priority),
  due: (a, b) => {
    const av = a.dueAt ? new Date(a.dueAt).getTime() : Infinity;
    const bv = b.dueAt ? new Date(b.dueAt).getTime() : Infinity;
    return av - bv;
  },
};

/* ── Main component ──────────────────────────────────────────────────── */

export const TaskList = ({
  tasks,
  workspaceSlug: _workspaceSlug,
  statuses,
  members,
  tags,
  onOpenTask,
  onUpdateTask,
  onReorderTask,
  onCreateTask,
  isWorkspace = false,
  initialGroupBy = 'status',
  density = 'comfortable',
}: TaskListProps) => {
  const [groupBy, setGroupBy] = useState<GroupByKey>(initialGroupBy);
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  // Single in-flight composer at a time, keyed by group spec key.
  const [composingGroupKey, setComposingGroupKey] = useState<string | null>(null);

  /**
   * Resolve a group spec key into the partial overrides for the API
   * payload. Status keys are category names (todo/in_progress/complete),
   * which we pair with the workspace's default status for that category;
   * assignee keys are member ULIDs (or `__unassigned`); priority keys
   * are priority literals; everything else just creates a plain task.
   */
  const submitCreate = async (groupKey: string, title: string): Promise<void> => {
    if (!onCreateTask) return;
    const overrides: CreateTaskOverrides = { title };
    if (groupBy === 'status') {
      const def = statuses.find((s) => s.category === groupKey && s.isDefault);
      if (def) overrides.status_id = def.id;
    } else if (groupBy === 'assignee' && groupKey !== '__unassigned') {
      overrides.assignee_id = groupKey;
    } else if (groupBy === 'priority') {
      overrides.priority = groupKey as Priority;
    }
    // due/project: no field override — server falls back to the
    // workspace's default todo status and a null assignee/due.
    await onCreateTask(overrides);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  // Search filter across title, code, tag names. Mirrors the prototype.
  const filtered = useMemo(() => {
    if (!search.trim()) return tasks;
    const q = search.toLowerCase();
    const tagsById = new Map(tags.map((t) => [t.id, t]));
    return tasks.filter(
      (t) =>
        t.title.toLowerCase().includes(q) ||
        t.code.toLowerCase().includes(q) ||
        t.tagIds.some((id) => (tagsById.get(id)?.name ?? '').toLowerCase().includes(q)),
    );
  }, [tasks, search, tags]);

  const sortFn = sortKey ? SORTABLE[sortKey] : null;
  const applySort = (arr: TaskRow[]): TaskRow[] => {
    if (!sortFn) return arr;
    const out = [...arr].sort(sortFn);
    return sortDir === 'desc' ? out.reverse() : out;
  };

  // ── Drag handlers — within-group reorder only ──────────────────────
  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDragId(null);
    if (!over) return;
    const activeId = String(active.id);
    const overId = String(over.id);
    if (activeId === overId) return;

    // Both must be in the same group; if `over` is a task we use its
    // position directly. Cross-group drags are rejected silently —
    // each group-by mode has different semantics for "what does
    // crossing imply" and we only ship within-group reorder for v1.
    const sourceTask = filtered.find((t) => t.id === activeId);
    const overTask = filtered.find((t) => t.id === overId);
    if (!sourceTask || !overTask) return;

    if (groupBy !== 'none') {
      const def = groupByDefs[groupBy];
      if (def.ofTask(sourceTask) !== def.ofTask(overTask)) return;
    } else {
      // Tree mode: must share parent.
      if ((sourceTask.parentTaskId ?? null) !== (overTask.parentTaskId ?? null)) return;
    }

    onReorderTask(activeId, overTask.position);
  };

  /* ── Empty state ─────────────────────────────────────────────────── */
  if (filtered.length === 0) {
    return (
      <ListShell isWorkspace={isWorkspace} density={density}>
        <ListSubbar
          groupBy={groupBy}
          onGroupByChange={setGroupBy}
          search={search}
          setSearch={setSearch}
          count={0}
          total={tasks.length}
        />
        <div
          className={cn(
            'flex flex-1 flex-col items-center justify-center gap-3.5',
            'text-foreground-muted px-6 py-16',
          )}
        >
          <div
            className={cn(
              'flex h-14 w-14 items-center justify-center rounded-2xl',
              'border border-[var(--glass-card-border)] bg-[var(--glass-card-bg)] text-foreground-faint',
            )}
          >
            <Search className="size-5" />
          </div>
          <h3 className="text-foreground m-0 text-[15px] font-medium">No matching tasks</h3>
          <p className="text-foreground-faint m-0 max-w-[320px] text-center text-[12.5px]">
            {search
              ? `Nothing matches "${search}". Try clearing your search.`
              : 'Nothing to show here yet.'}
          </p>
          {search ? (
            <div className="mt-1 flex gap-2">
              <button
                type="button"
                onClick={() => setSearch('')}
                className={cn(
                  'inline-flex h-7 items-center gap-1.5 rounded-lg border px-2.5 text-[12.5px]',
                  'border-[var(--glass-card-border)] bg-[var(--glass-card-bg)] text-foreground-muted',
                  'hover:text-foreground hover:border-foreground-faint',
                )}
              >
                Clear search
              </button>
            </div>
          ) : null}
        </div>
      </ListShell>
    );
  }

  /* ── Tree mode (groupBy === 'none') ──────────────────────────────── */
  if (groupBy === 'none') {
    const tree = buildTree(applySort(filtered));
    const renderNode = (node: TreeNode, depth: number): ReactNode => {
      const isExp = expanded[node.id] !== false;
      const has = node.children.length > 0;
      return (
        <div key={node.id}>
          <ListRow
            task={node}
            depth={depth}
            hasChildren={has}
            expanded={isExp}
            onToggleExpand={() =>
              setExpanded((e) => ({ ...e, [node.id]: !isExp }))
            }
            members={members}
            statuses={statuses}
            tags={tags}
            isWorkspace={isWorkspace}
            isDragging={activeDragId === node.id}
            onOpen={onOpenTask}
            onUpdate={onUpdateTask}
          />
          {has && isExp ? node.children.map((c) => renderNode(c, depth + 1)) : null}
        </div>
      );
    };
    return (
      <ListShell isWorkspace={isWorkspace} density={density}>
        <ListSubbar
          groupBy={groupBy}
          onGroupByChange={setGroupBy}
          search={search}
          setSearch={setSearch}
          count={filtered.length}
          total={tasks.length}
        />
        <ColHead
          isWorkspace={isWorkspace}
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={(k) => {
            if (sortKey === k) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
            else {
              setSortKey(k);
              setSortDir('asc');
            }
          }}
        />
        <div className="flex-1 overflow-y-auto">
          <DndContext
            sensors={sensors}
            modifiers={[restrictToVerticalAxis]}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            {tree.map((n) => renderNode(n, 0))}
            <DragOverlay>
              {activeDragId ? (
                <DragGhost task={filtered.find((t) => t.id === activeDragId) ?? null} />
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>
      </ListShell>
    );
  }

  /* ── Grouped mode ────────────────────────────────────────────────── */

  const def = groupByDefs[groupBy];
  const groupSpecs = def.groups({ members, isWorkspace, tasks });
  const buckets: Record<string, TaskRow[]> = {};
  for (const g of groupSpecs) buckets[g.key] = [];
  for (const t of filtered) {
    const k = def.ofTask(t);
    if (!buckets[k]) buckets[k] = [];
    buckets[k].push(t);
  }

  return (
    <ListShell isWorkspace={isWorkspace} density={density}>
      <ListSubbar
        groupBy={groupBy}
        onGroupByChange={setGroupBy}
        search={search}
        setSearch={setSearch}
        count={filtered.length}
        total={tasks.length}
      />
      <ColHead
        isWorkspace={isWorkspace}
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={(k) => {
          if (sortKey === k) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
          else {
            setSortKey(k);
            setSortDir('asc');
          }
        }}
      />
      <div className="flex-1 overflow-y-auto">
        <DndContext
          sensors={sensors}
          modifiers={[restrictToVerticalAxis]}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          {groupSpecs.map((g) => {
            const arr = applySort(buckets[g.key] ?? []);
            // Hide empty groups EXCEPT the three status swimlanes — those
            // are always shown (matches prototype's behaviour).
            if (arr.length === 0 && groupBy !== 'status') return null;
            const isCollapsed = collapsed[g.key] === true;
            const tintClass =
              groupBy === 'status'
                ? GROUP_TINT_CLASS[g.classKey ?? 'todo']
                : '';
            return (
              <div key={g.key} className="border-b border-border">
                <div
                  className={cn(
                    'flex cursor-pointer items-center gap-2.5 select-none',
                    'border-b border-border px-6 py-2.5 transition-[background] duration-100',
                    'hover:bg-[var(--hover-bg,oklch(0.96_0_0))]',
                    tintClass,
                  )}
                  onClick={() =>
                    setCollapsed((c) => ({ ...c, [g.key]: !isCollapsed }))
                  }
                >
                  <ChevronDown
                    className={cn(
                      'text-foreground-faint size-3.5 transition-transform duration-[180ms] ease-[cubic-bezier(0.2,0.7,0.2,1)]',
                      isCollapsed && '-rotate-90',
                    )}
                  />
                  <span
                    aria-hidden="true"
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={{ background: g.color }}
                  />
                  <span className="text-foreground text-[13px] font-semibold tracking-[-0.005em] whitespace-nowrap">
                    {g.label}
                  </span>
                  <span
                    className={cn(
                      'text-foreground-faint rounded-full border px-[7px] py-px font-mono text-[11.5px] tabular-nums',
                      'border-border bg-secondary',
                    )}
                  >
                    {arr.length}
                  </span>
                  {onCreateTask ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Force-uncollapse when opening the composer in a
                        // collapsed group so the user actually sees the
                        // input below. No-op if already expanded.
                        if (isCollapsed) {
                          setCollapsed((c) => ({ ...c, [g.key]: false }));
                        }
                        setComposingGroupKey(g.key);
                      }}
                      className={cn(
                        'text-foreground-faint hover:bg-accent hover:text-foreground ml-auto inline-flex items-center gap-1 rounded-md border-0 bg-transparent px-2 py-1 text-[11.5px]',
                      )}
                    >
                      <Plus className="size-3" />
                      Add
                    </button>
                  ) : null}
                </div>
                {!isCollapsed && composingGroupKey === g.key && onCreateTask ? (
                  <ListInlineComposer
                    placeholder={`New task · ${g.label}`}
                    onSubmit={(title) => submitCreate(g.key, title)}
                    onClose={() => setComposingGroupKey(null)}
                  />
                ) : null}
                {!isCollapsed
                  ? arr.map((t) => (
                      <ListRow
                        key={t.id}
                        task={t}
                        depth={0}
                        hasChildren={false}
                        members={members}
                        statuses={statuses}
                        tags={tags}
                        isWorkspace={isWorkspace}
                        isDragging={activeDragId === t.id}
                        onOpen={onOpenTask}
                        onUpdate={onUpdateTask}
                      />
                    ))
                  : null}
                {!isCollapsed && arr.length === 0 && composingGroupKey !== g.key ? (
                  <div className="text-foreground-faint px-6 py-3 text-[12px]">
                    No tasks
                  </div>
                ) : null}
              </div>
            );
          })}
          <DragOverlay>
            {activeDragId ? (
              <DragGhost task={filtered.find((t) => t.id === activeDragId) ?? null} />
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
    </ListShell>
  );
};

const GROUP_TINT_CLASS: Record<StatusCategory, string> = {
  todo: 'bg-[var(--tint-todo)]',
  in_progress: 'bg-[var(--tint-in-progress)]',
  complete: 'bg-[var(--tint-done)]',
};

/* ── Shell + grid template ───────────────────────────────────────────
 *
 * `--list-grid` defines the row + colhead grid template. Project mode
 * uses 9 cells; workspace mode adds a project chip column after title.
 *
 * Responsive cuts (matching the prototype's @media rules):
 *   ≤1180px: drop tags + time
 *   ≤980px:  also drop priority
 *
 * Tailwind doesn't natively support "set a CSS var per breakpoint",
 * so the responsive variants are explicit container-query-style
 * arbitrary values applied at `xl:` and `md:` breakpoints below.
 *
 * The same template applies to .v1-list-row and .v1-list-colhead —
 * Grid header alignment with the rows requires both to read the same
 * --list-grid value. CSS variable inheritance handles that.
 */

interface ListShellProps {
  children: ReactNode;
  isWorkspace: boolean;
  density: 'comfortable' | 'dense';
}

const ListShell = ({ children, isWorkspace, density }: ListShellProps) => {
  // Project-mode default. Workspace mode inserts a 78px project chip
  // column AFTER the title cell (4th position). `data-workspace` +
  // `data-density` are on the root for any future CSS hooks; the
  // grid template is applied via a CSS variable inline.
  const gridTemplate = isWorkspace
    ? '18px 62px minmax(160px, 1fr) 78px 88px minmax(100px, 0.7fr) 28px 78px 78px 20px'
    : '18px 62px minmax(160px, 1fr) 88px minmax(100px, 0.7fr) 28px 78px 78px 20px';

  return (
    <div
      data-workspace={isWorkspace}
      data-density={density}
      className="relative flex min-h-0 flex-1 flex-col"
      style={{ '--list-grid': gridTemplate } as CSSProperties}
    >
      {children}
    </div>
  );
};

/* ── Sub-toolbar ─────────────────────────────────────────────────────── */

interface ListSubbarProps {
  groupBy: GroupByKey;
  onGroupByChange: (next: GroupByKey) => void;
  search: string;
  setSearch: (next: string) => void;
  count: number;
  total: number;
}

const GROUP_BY_OPTIONS: Array<{ key: GroupByKey; label: string }> = [
  { key: 'status', label: 'Status' },
  { key: 'assignee', label: 'Assignee' },
  { key: 'priority', label: 'Priority' },
  { key: 'due', label: 'Due date' },
  { key: 'project', label: 'Project' },
  { key: 'none', label: 'None' },
];

const ListSubbar = ({
  groupBy,
  onGroupByChange,
  search,
  setSearch,
  count,
  total,
}: ListSubbarProps) => {
  const groupLabel = GROUP_BY_OPTIONS.find((o) => o.key === groupBy)?.label ?? 'Status';
  const inputRef = useRef<HTMLInputElement | null>(null);

  // `/` shortcut focuses the search input — common convention
  // (Linear, GitHub). Skip if the user is already typing in a field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/') return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      e.preventDefault();
      inputRef.current?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div
      className={cn(
        'flex items-center gap-2.5 px-6 py-2.5',
        'border-b border-border bg-background',
      )}
    >
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              'inline-flex h-[30px] cursor-pointer items-center gap-1.5 rounded-lg px-2.5 text-[12.5px]',
              'border border-border bg-background text-foreground transition-colors duration-100',
              'hover:bg-accent',
            )}
          >
            <FilterIcon className="text-foreground-faint size-3" />
            <span className="text-foreground-faint text-[11.5px]">Group:</span>
            <span className="font-medium">{groupLabel}</span>
            <ChevronDown className="text-foreground-faint size-2.5" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={6}
          className={cn(
            'bg-background text-foreground min-w-[180px] rounded-[10px] border p-1.5',
            'border-border shadow-[0_12px_32px_oklch(0_0_0/0.18)] ring-0',
          )}
        >
          <div className="text-foreground-faint px-2 pt-1.5 pb-1 text-[10.5px] font-semibold tracking-[0.06em] uppercase">
            Group by
          </div>
          {GROUP_BY_OPTIONS.map((o) => (
            <button
              key={o.key}
              type="button"
              onClick={() => onGroupByChange(o.key)}
              className={cn(
                'text-foreground hover:bg-accent flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12.5px]',
              )}
            >
              <span className="flex-1">{o.label}</span>
              {o.key === groupBy ? (
                <span className="text-foreground-faint">✓</span>
              ) : null}
            </button>
          ))}
        </PopoverContent>
      </Popover>

      <div
        className={cn(
          'flex h-[30px] flex-1 items-center gap-2 rounded-lg px-2.5',
          'bg-secondary text-foreground-faint border border-transparent transition-colors duration-100',
          'focus-within:border-[var(--brand-accent)] focus-within:bg-background',
        )}
      >
        <Search className="size-3" />
        <input
          ref={inputRef}
          placeholder="Search tasks…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={cn(
            'text-foreground placeholder:text-foreground-faint h-full flex-1 border-0 bg-transparent text-[12.5px] outline-none',
          )}
        />
        <kbd
          className={cn(
            'border-border bg-background text-foreground-faint inline-flex items-center rounded-[4px] border px-1 font-mono text-[10px]',
          )}
        >
          /
        </kbd>
      </div>

      <span className="text-foreground-faint text-[11.5px] tabular-nums">
        {count} of {total}
      </span>
    </div>
  );
};

/* ── Column header ───────────────────────────────────────────────────── */

interface ColHeadProps {
  isWorkspace: boolean;
  sortKey: SortKey | null;
  sortDir: 'asc' | 'desc';
  onSort: (k: SortKey) => void;
}

const ColHead = ({ isWorkspace, sortKey, sortDir, onSort }: ColHeadProps) => {
  return (
    <div
      className={cn(
        'sticky top-0 z-[5] grid items-center gap-4 px-6 py-2',
        'border-b border-border bg-background',
        'text-foreground-faint text-[10.5px] font-semibold tracking-[0.06em] uppercase',
      )}
      style={{ gridTemplateColumns: 'var(--list-grid)' }}
    >
      <span />
      <ColHeadCell label="Code" k="code" sortKey={sortKey} sortDir={sortDir} onSort={onSort} sortable />
      <ColHeadCell
        label="Title · Updated"
        k="title"
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={onSort}
        sortable
      />
      {isWorkspace ? <span>Project</span> : null}
      <ColHeadCell
        label="Priority"
        k="priority"
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={onSort}
        sortable
      />
      <span>Tags</span>
      <span>Who</span>
      <ColHeadCell label="Due" k="due" sortKey={sortKey} sortDir={sortDir} onSort={onSort} sortable />
      <span>Logged / Est</span>
      <span />
    </div>
  );
};

interface ColHeadCellProps {
  k: SortKey;
  label: string;
  sortKey: SortKey | null;
  sortDir: 'asc' | 'desc';
  onSort: (k: SortKey) => void;
  sortable: boolean;
}

const ColHeadCell = ({ k, label, sortKey, sortDir, onSort, sortable }: ColHeadCellProps) => {
  const isSorted = sortKey === k;
  return (
    <div
      onClick={sortable ? () => onSort(k) : undefined}
      className={cn(
        'inline-flex items-center gap-1 select-none',
        sortable ? 'cursor-pointer' : 'cursor-default',
        sortable && 'hover:text-foreground-muted',
        isSorted && 'text-foreground',
      )}
    >
      <span>{label}</span>
      {sortable && isSorted ? (
        <svg width="10" height="10" viewBox="0 0 10 10" className="opacity-70">
          {sortDir === 'asc' ? (
            <path d="M3 6 L5 4 L7 6" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          ) : (
            <path d="M3 4 L5 6 L7 4" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          )}
        </svg>
      ) : null}
    </div>
  );
};

/* ── Row ─────────────────────────────────────────────────────────────── */

interface ListRowProps {
  task: TaskRow;
  depth: number;
  hasChildren: boolean;
  expanded?: boolean;
  onToggleExpand?: () => void;
  isDragging: boolean;
  members: MemberOption[];
  statuses: StatusOption[];
  tags: TagOption[];
  isWorkspace: boolean;
  onOpen: (taskUlid: string) => void;
  onUpdate: (taskUlid: string, patch: { status_id?: string; assignee_id?: string | null }) => void;
}

const ListRow = ({
  task,
  depth,
  hasChildren,
  expanded,
  onToggleExpand,
  isDragging,
  members,
  statuses,
  tags,
  isWorkspace,
  onOpen,
  onUpdate,
}: ListRowProps) => {
  const due = formatDue(task.dueAt);
  const priority = PRIORITY_INFO[task.priority];
  const assignee = task.assigneeId ? members.find((m) => m.id === task.assigneeId) : undefined;
  const tagDefs = task.tagIds
    .map((id) => tags.find((t) => t.id === id))
    .filter((t): t is TagOption => Boolean(t));

  // Drag handle on the leading column. The whole row is the click
  // target for opening the detail; clicks on interactive children
  // (status dot, avatar, more button, leading) are consumed.
  const { attributes, listeners, setNodeRef: setDragRef } = useDraggable({
    id: task.id,
  });
  const { setNodeRef: setDropRef } = useDroppable({ id: task.id });

  const setRefs = (el: HTMLDivElement | null): void => {
    setDragRef(el);
    setDropRef(el);
  };

  return (
    <div
      ref={setRefs}
      data-depth={depth}
      data-task-id={task.id}
      onClick={(e) => {
        const t = e.target as HTMLElement;
        if (
          t.closest('button, [data-row-leading], [data-row-status-dot], [data-row-assignee]')
        ) {
          return;
        }
        onOpen(task.id);
      }}
      className={cn(
        'group/row relative grid cursor-pointer items-center gap-4',
        'border-b border-border px-6 py-2.5 transition-[background,transform] duration-100',
        'hover:bg-[var(--hover-bg,oklch(0.96_0_0))]',
        isDragging && 'opacity-50',
      )}
      style={{ gridTemplateColumns: 'var(--list-grid)' }}
    >
      {/* Subtask depth guide — left vertical line(s) at indent positions. */}
      {depth > 0 ? (
        <span
          aria-hidden="true"
          className="bg-border pointer-events-none absolute top-0 bottom-0 w-px"
          style={{ left: 24 + 12 * depth }}
        />
      ) : null}

      {/* Leading: chevron (when has children) OR drag handle */}
      <div
        data-row-leading
        {...(hasChildren ? {} : { ...attributes, ...listeners })}
        onClick={(e) => {
          e.stopPropagation();
          if (hasChildren && onToggleExpand) onToggleExpand();
        }}
        title={hasChildren ? 'Toggle subtasks' : 'Drag to reorder'}
        className={cn(
          'text-foreground-faint inline-flex h-[18px] w-[18px] cursor-grab items-center justify-center rounded-[4px]',
          'hover:text-foreground hover:bg-[var(--hover-bg,oklch(0.96_0_0))]',
          'active:cursor-grabbing',
        )}
      >
        {hasChildren ? (
          <ChevronRight
            className={cn(
              'size-2.5 transition-transform duration-[180ms] ease-[cubic-bezier(0.2,0.7,0.2,1)]',
              expanded && 'rotate-90',
            )}
          />
        ) : (
          <GripVertical className="size-2.5 opacity-45" />
        )}
      </div>

      {/* Code + status dot */}
      <div className="text-foreground-muted inline-flex items-center gap-1.5 font-mono text-[11px] font-medium">
        <span
          data-row-status-dot
          onClick={(e) => {
            e.stopPropagation();
            // Trigger via the popover wrapper below.
            const target = e.currentTarget as HTMLElement;
            target.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
          }}
          className="block h-2 w-2 shrink-0 cursor-pointer rounded-full transition-transform duration-[120ms] hover:scale-[1.4]"
          style={{ background: task.statusColor }}
          title={`${task.statusName} — click to change`}
        />
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              onClick={(e) => e.stopPropagation()}
              aria-label="Change status"
              className="text-foreground-muted text-left font-mono"
            >
              {task.code}
            </button>
          </PopoverTrigger>
          <InlineStatusPickerContent
            currentStatusId={task.statusId}
            statuses={statuses}
            onPick={(statusId) => onUpdate(task.id, { status_id: statusId })}
          />
        </Popover>
      </div>

      {/* Title + meta column */}
      <div className="flex min-w-0 flex-col gap-[3px]">
        <div className="text-foreground flex items-center gap-1.5 truncate text-[13.5px] leading-[1.35] font-medium tracking-[-0.005em]">
          <span className="truncate">{task.title}</span>
          <span className="text-foreground-faint inline-flex shrink-0 items-center gap-1.5 text-[11px] font-normal before:opacity-60 before:content-['·']">
            {task.statusName}
          </span>
        </div>
        <div className="text-foreground-faint flex flex-wrap items-center gap-3 text-[11px]">
          {task.commentCount > 0 ? (
            <span className="inline-flex items-center gap-1">
              <CommentIcon /> {task.commentCount}
            </span>
          ) : null}
          {task.pullRequestCount > 0 ? (
            <span className="inline-flex items-center gap-1">
              <PrIcon /> {task.pullRequestCount}
            </span>
          ) : null}
          <span className="inline-flex items-center gap-1 tabular-nums whitespace-nowrap">
            Updated {formatRelativeTime(task.updatedAt) || 'recently'}
          </span>
        </div>
      </div>

      {/* Project (workspace mode) */}
      {isWorkspace ? (
        <div className="min-w-0">
          {task.projectName ? (
            <span
              className={cn(
                'text-foreground-muted inline-flex max-w-full items-center gap-1.5 truncate rounded-[5px] border px-2 py-px text-[11.5px]',
                'border-border bg-secondary',
              )}
              title={task.projectName}
            >
              <span
                aria-hidden="true"
                className="h-3 w-3 shrink-0 rounded-[3px]"
                style={{ background: task.projectColor ?? 'var(--foreground-muted)' }}
              />
              {task.projectCode ?? task.projectName}
            </span>
          ) : null}
        </div>
      ) : null}

      {/* Priority */}
      <div>
        <span
          className={cn(
            'inline-flex items-center gap-1.5 rounded-[5px] border px-1.5 py-0.5 text-[11.5px]',
            'border-border bg-secondary',
          )}
          style={{ color: priority.color }}
        >
          <span className="font-mono text-[11px]">{priority.glyph}</span>
          {priority.name === 'No priority' ? '—' : priority.name}
        </span>
      </div>

      {/* Tags */}
      <div className="flex items-center gap-1 overflow-hidden">
        {tagDefs.length === 0 ? (
          <span className="text-foreground-faint text-[11.5px]">—</span>
        ) : (
          <>
            {tagDefs.slice(0, 2).map((t) => (
              <span
                key={t.id}
                className={cn(
                  'text-foreground-muted inline-flex shrink-0 items-center gap-1 rounded-full bg-[var(--surface-quiet)] px-[7px] py-px text-[10.5px] font-medium',
                )}
              >
                <span
                  aria-hidden="true"
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ background: t.color }}
                />
                {t.name}
              </span>
            ))}
            {tagDefs.length > 2 ? (
              <span className="text-foreground-faint shrink-0 text-[11px]">
                +{tagDefs.length - 2}
              </span>
            ) : null}
          </>
        )}
      </div>

      {/* Assignee */}
      <Popover>
        <PopoverTrigger asChild>
          <div
            data-row-assignee
            onClick={(e) => e.stopPropagation()}
            className="inline-flex cursor-pointer"
            title={assignee ? `${assignee.name ?? assignee.email} — click to change` : 'Unassigned — click to assign'}
          >
            {assignee ? (
              <MemberAvatar
                id={assignee.id}
                name={assignee.name}
                email={assignee.email}
                size="xs"
                className="!h-[22px] !w-[22px] !text-[9px] shadow-[0_0_0_2px_var(--background)]"
              />
            ) : (
              <span
                className={cn(
                  'border-foreground-faint text-foreground-faint inline-flex h-[22px] w-[22px] items-center justify-center rounded-full border border-dashed text-[10px]',
                )}
              >
                ?
              </span>
            )}
          </div>
        </PopoverTrigger>
        <InlineAssigneePickerContent
          currentAssigneeId={task.assigneeId}
          members={members}
          onPick={(id) => onUpdate(task.id, { assignee_id: id })}
        />
      </Popover>

      {/* Due */}
      <div
        className={cn(
          'text-foreground-muted text-[11.5px] tabular-nums',
          due?.proximity === 'overdue' && 'text-[oklch(0.55_0.2_25)]',
          due?.proximity === 'past' && 'text-[oklch(0.55_0.2_25)]',
          due?.proximity === 'today' && 'text-[oklch(0.65_0.18_50)]',
        )}
      >
        {due ? due.label : <span className="text-foreground-faint">—</span>}
      </div>

      {/* Time — `<strong>logged</strong> /est` with /est at 50% opacity */}
      <div className="text-foreground-faint font-mono text-[11px] tabular-nums">
        {task.loggedMinutes > 0 || task.estimateMinutes !== null ? (
          <>
            <strong className="text-foreground-muted font-medium">
              {minutesToShort(task.loggedMinutes)}
            </strong>
            {task.estimateMinutes !== null ? (
              <span className="opacity-50"> / {minutesToShort(task.estimateMinutes)}</span>
            ) : null}
          </>
        ) : (
          <span>—</span>
        )}
      </div>

      {/* Trail — More button, hover-revealed */}
      <button
        type="button"
        aria-label="More"
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'text-foreground-faint hover:bg-[var(--hover-bg,oklch(0.96_0_0))] hover:text-foreground inline-flex h-[22px] w-[22px] items-center justify-center rounded-[4px]',
          'cursor-pointer border-0 bg-transparent opacity-0 transition-opacity duration-100',
          'group-hover/row:opacity-100',
        )}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="5" cy="12" r="1" />
          <circle cx="12" cy="12" r="1" />
          <circle cx="19" cy="12" r="1" />
        </svg>
      </button>
    </div>
  );
};

/* ── Inline pickers ──────────────────────────────────────────────────── */

interface InlineStatusPickerContentProps {
  currentStatusId: string;
  statuses: StatusOption[];
  onPick: (statusId: string) => void;
}

const InlineStatusPickerContent = ({
  currentStatusId,
  statuses,
  onPick,
}: InlineStatusPickerContentProps) => {
  // Group statuses by category — same lane order as kanban swimlanes.
  const byCat: Record<StatusCategory, StatusOption[]> = {
    todo: [],
    in_progress: [],
    complete: [],
  };
  for (const s of statuses) byCat[s.category].push(s);

  return (
    <PopoverContent
      align="start"
      sideOffset={6}
      className={cn(
        'bg-background text-foreground min-w-[200px] rounded-[10px] border p-1.5',
        'border-border shadow-[0_12px_32px_oklch(0_0_0/0.18)] ring-0',
      )}
    >
      {SWIMLANE_ORDER.map((lane) => (
        <div key={lane.key}>
          <div className="text-foreground-faint px-2 pt-1.5 pb-1 text-[10px] font-semibold tracking-[0.06em] uppercase">
            {lane.label}
          </div>
          {byCat[lane.key].map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => onPick(s.id)}
              className={cn(
                'text-foreground hover:bg-accent flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12.5px]',
              )}
            >
              <span
                aria-hidden="true"
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ background: s.color }}
              />
              {s.name}
              {s.id === currentStatusId ? (
                <span className="text-foreground-faint ml-auto">✓</span>
              ) : null}
            </button>
          ))}
        </div>
      ))}
    </PopoverContent>
  );
};

interface InlineAssigneePickerContentProps {
  currentAssigneeId: string | null;
  members: MemberOption[];
  onPick: (id: string | null) => void;
}

const InlineAssigneePickerContent = ({
  currentAssigneeId,
  members,
  onPick,
}: InlineAssigneePickerContentProps) => (
  <PopoverContent
    align="start"
    sideOffset={6}
    className={cn(
      'bg-background text-foreground min-w-[200px] rounded-[10px] border p-1.5',
      'border-border shadow-[0_12px_32px_oklch(0_0_0/0.18)] ring-0',
    )}
  >
    <div className="text-foreground-faint px-2 pt-1.5 pb-1 text-[10px] font-semibold tracking-[0.06em] uppercase">
      Assignee
    </div>
    {members.map((m) => (
      <button
        key={m.id}
        type="button"
        onClick={() => onPick(m.id)}
        className="text-foreground hover:bg-accent flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12.5px]"
      >
        <MemberAvatar
          id={m.id}
          name={m.name}
          email={m.email}
          size="xs"
          className="!h-[18px] !w-[18px] !text-[8px]"
        />
        {m.name ?? m.email ?? 'Unknown'}
        {m.id === currentAssigneeId ? (
          <span className="text-foreground-faint ml-auto">✓</span>
        ) : null}
      </button>
    ))}
    <button
      type="button"
      onClick={() => onPick(null)}
      className={cn(
        'text-foreground-muted mt-1 flex w-full items-center gap-2 border-t px-2 pt-2 pb-1.5 text-left text-[12.5px]',
        'border-border hover:bg-accent rounded-md',
      )}
    >
      <span className="border-foreground-faint inline-flex h-[18px] w-[18px] items-center justify-center rounded-full border border-dashed" />
      Unassigned
    </button>
  </PopoverContent>
);

/* ── Inline group composer ───────────────────────────────────────────── */

interface ListInlineComposerProps {
  placeholder: string;
  onSubmit: (title: string) => Promise<void>;
  onClose: () => void;
}

/**
 * Linear-style inline add-row, rendered just below the group header
 * (above the first row) when the user clicks "+ Add". Mirrors the
 * kanban swimlane composer's behavior:
 *   - Enter submits (clears + keeps open for back-to-back additions)
 *   - Esc closes
 *   - Outside-click closes (saves the draft if non-empty)
 *
 * Glass-card surface matches `.v1-composer` from the prototype's
 * shared kanban styles — same translucent fill, blur, dashed kbd-hint
 * divider — sized to fit the list's row width.
 *
 * Note: lives in the row column of the list, NOT the grid template.
 * The composer is a single full-width div padded to align with row
 * content (px-6) but the input itself gets its own card frame so it
 * reads as a "this is being created" surface, not "this is a row".
 */
const ListInlineComposer = ({ placeholder, onSubmit, onClose }: ListInlineComposerProps) => {
  const [value, setValue] = useState('');
  const [pending, setPending] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Outside-click in the capture phase so a click on a sibling
  // group's "+ Add" gets a clean save+close before the new
  // composer opens elsewhere.
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const node = inputRef.current?.closest('[data-list-composer-root]');
      if (node && !node.contains(e.target as Node)) {
        const trimmed = value.trim();
        if (trimmed.length > 0) {
          void submit(trimmed, { keepOpen: false });
        } else {
          onClose();
        }
      }
    };
    document.addEventListener('mousedown', handleClick, true);
    return () => document.removeEventListener('mousedown', handleClick, true);
    // submit rebinds per render via closure on `value`; intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, onClose]);

  const submit = async (
    title: string,
    opts: { keepOpen: boolean } = { keepOpen: true },
  ): Promise<void> => {
    if (pending) return;
    setPending(true);
    try {
      await onSubmit(title);
      setValue('');
      if (!opts.keepOpen) onClose();
      else inputRef.current?.focus();
    } finally {
      setPending(false);
    }
  };

  return (
    <div
      data-list-composer-root
      className="border-b border-border bg-background px-6 py-2.5"
    >
      <div
        className={cn(
          'rounded-xl border px-3 py-2.5',
          'border-[var(--glass-card-border)] bg-[var(--glass-card-bg)]',
          'shadow-[var(--glass-card-shadow)]',
        )}
      >
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              const trimmed = value.trim();
              if (trimmed.length > 0) void submit(trimmed);
            } else if (e.key === 'Escape') {
              e.preventDefault();
              onClose();
            }
          }}
          placeholder={placeholder}
          autoFocus
          disabled={pending}
          className={cn(
            'block w-full border-0 bg-transparent px-0 py-1 text-[13.5px] outline-none',
            'placeholder:text-foreground-faint text-foreground',
          )}
        />
        <div
          className={cn(
            'text-foreground-faint mt-2 flex items-center justify-between border-t border-dashed pt-2 text-[11px]',
            'border-[var(--glass-card-border)]',
          )}
        >
          <span>
            <kbd className="border-border bg-secondary text-foreground-faint mx-0.5 inline-flex h-[18px] items-center rounded-[4px] border px-1 font-mono text-[10px]">
              Enter
            </kbd>{' '}
            to add ·{' '}
            <kbd className="border-border bg-secondary text-foreground-faint mx-0.5 inline-flex h-[18px] items-center rounded-[4px] border px-1 font-mono text-[10px]">
              Esc
            </kbd>{' '}
            to close
          </span>
          <button
            type="button"
            onClick={onClose}
            className="text-foreground-muted hover:text-foreground border-0 bg-transparent text-[11px]"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Drag overlay ghost ──────────────────────────────────────────────── */

const DragGhost = ({ task }: { task: TaskRow | null }) => {
  if (!task) return null;
  return (
    <div
      className={cn(
        'rounded-xl border bg-[var(--glass-card-bg)] px-3 py-2 backdrop-blur-md',
        'rotate-[1.5deg] scale-[1.02] border-[var(--brand-accent)]',
        'shadow-[0_24px_60px_oklch(0_0_0/0.25)]',
      )}
    >
      <div className="text-foreground-muted mb-0.5 inline-flex items-center gap-1.5 font-mono text-[10.5px]">
        <span className="h-2 w-2 rounded-full" style={{ background: task.statusColor }} />
        {task.code}
      </div>
      <div className="text-foreground text-[13.5px] font-medium tracking-[-0.005em]">
        {task.title}
      </div>
    </div>
  );
};

/* ── Tree builder for ungrouped mode ─────────────────────────────────── */

interface TreeNode extends TaskRow {
  children: TreeNode[];
}

function buildTree(tasks: TaskRow[]): TreeNode[] {
  const byId = new Map<string, TreeNode>();
  for (const t of tasks) byId.set(t.id, { ...t, children: [] });
  const roots: TreeNode[] = [];
  for (const t of tasks) {
    const node = byId.get(t.id);
    if (!node) continue;
    if (t.parentTaskId && byId.has(t.parentTaskId)) {
      byId.get(t.parentTaskId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
}

/* ── Inline icons (lucide-equivalent paths inline so we can scale them
 *    sub-12px without vendoring a separate icon for the meta row) ──── */

const CommentIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const PrIcon = () => (
  <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
    <circle cx="4" cy="3" r="2" stroke="currentColor" strokeWidth="1.5" />
    <path d="M4 5v8" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="4" cy="13" r="2" stroke="currentColor" strokeWidth="1.5" />
    <path d="M10 8h2v5" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="12" cy="3" r="2" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

/* ── Minutes → compact "Xh Ym" — same shape as the prototype's
 *    seed strings ("4h", "3h 20m", "45m"). Keeping the formatter
 *    inline so the file is self-contained for easy diffing against
 *    the prototype. ─────────────────────────────────────────────── */

function minutesToShort(min: number): string {
  if (min === 0) return '0h';
  const safe = Math.max(0, Math.floor(min));
  const hours = Math.floor(safe / 60);
  const mins = safe % 60;
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}
